import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ArticleCard from '@/components/base/ArticleCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles } from '@/mocks/data';

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading font-bold text-xl text-foreground-950 mb-2">
              文章未找到
            </h1>
            <p className="text-foreground-600 text-sm mb-6">
              您访问的文章不存在或已被移除
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryLabel =
    article.category === 'latest-address' ? '最新地址' :
    article.category === 'access-guide' ? '访问指南' :
    article.category === 'popular-recommend' ? '热门推荐' :
    article.category === 'new-releases' ? '最新上架' :
    article.category === 'genres' ? '分类浏览' : '帮助中心';

  const relatedArticles = articles
    .filter((a) => a.slug !== article.slug && a.category === article.category)
    .slice(0, 3);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        datePublished: article.date,
        url: `https://jmcomic-zzz.one/article/${article.slug}`,
        author: {
          '@type': 'Organization',
          name: 'JM漫画导航',
        },
        publisher: {
          '@type': 'Organization',
          name: 'JM漫画导航 - jmcomic-zzz.one',
        },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: '首页',
            item: 'https://jmcomic-zzz.one/',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: '分类浏览',
            item: 'https://jmcomic-zzz.one/category',
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: article.title,
          },
        ],
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Navbar />

      <main>
        <article className="pt-24 pb-8 md:pt-28 md:pb-10 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '分类浏览', href: '/category' },
                { label: article.title },
              ]}
            />
            <span className="inline-block mt-4 px-2.5 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-md">
              {categoryLabel}
            </span>
            <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mt-3 mb-3 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                阅读时间 {article.readTime}
              </span>
              <div className="flex items-center gap-1.5 flex-wrap">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2 py-0.5 bg-background-200 text-foreground-600 rounded text-xs"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        <section className="py-8 md:py-12 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="mb-8 rounded-lg overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                title={article.title}
                className="w-full h-48 md:h-64 object-cover"
              />
            </div>
            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed
                [&_h2]:font-heading [&_h2]:font-bold [&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:text-foreground-950 [&_h2]:mt-8 [&_h2]:mb-4
                [&_h3]:font-heading [&_h3]:font-semibold [&_h3]:text-lg [&_h3]:text-foreground-900 [&_h3]:mt-6 [&_h3]:mb-3
                [&_p]:mb-4 [&_p]:leading-relaxed
                [&_ul]:mb-4 [&_ul]:pl-5 [&_ul]:list-disc [&_ul]:space-y-1
                [&_li]:text-foreground-700 [&_li]:leading-relaxed
                [&_strong]:text-foreground-900 [&_strong]:font-semibold"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </div>
        </section>

        {relatedArticles.length > 0 && (
          <section className="py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-6">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {relatedArticles.map((ra) => (
                  <ArticleCard key={ra.slug} article={ra} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}